import axios from 'axios';
import { encryptData } from '../components/utils/cryptoUtils';
import CryptoJS from 'crypto-js';
import { toast } from 'react-toastify';

const client = axios.create({});
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ======================================================
// Toast IDs
// ======================================================

const TOAST_IDS = {
  SESSION_EXPIRED: 'session-expired',
  AUTH_ERROR: 'auth-error',
};

// ======================================================
// Prevent Multiple Redirects
// ======================================================

let isRedirecting = false;

// ======================================================
// Session Expiry Check
// ======================================================

const isSessionExpired = () => {
  const expiry = localStorage.getItem('sessionExpiry');

  if (!expiry) return false;

  return Date.now() > Number(expiry);
};

// ======================================================
// Redirect To Login
// ======================================================

const redirectToLogin = (delayMs = 3000) => {
  if (isRedirecting) return;

  isRedirecting = true;

  setTimeout(() => {
    localStorage.clear();
    sessionStorage.clear();

    window.location.href = '/';
  }, delayMs);
};

// ======================================================
// User Activity Tracking
// ======================================================

const updateUserActivity = () => {
  localStorage.setItem('lastActivity', Date.now());

  // If user becomes active after token expiry
  if (isSessionExpired()) {
    toast.error('Session expired. Please log in again.', {
      toastId: TOAST_IDS.SESSION_EXPIRED,
      autoClose: 3000,
    });

    redirectToLogin(3000);
  }
};

// ======================================================
// Register Activity Events
// ======================================================

const EVENTS = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress', 'touchstart'];

let listenersInitialized = false;

const initializeActivityListeners = () => {
  if (listenersInitialized) return;

  listenersInitialized = true;

  EVENTS.forEach((event) => {
    window.addEventListener(event, updateUserActivity);
  });
};

// Initialize only once
initializeActivityListeners();

// ======================================================
// Main Request Function
// ======================================================

const request = async (options) => {
  const pyuseridentifier =
    localStorage.getItem('pyuseridentifier') || localStorage.getItem('pyUser');

  const isSSO = window.isSSO;
  const password = localStorage.getItem('password');
  // ======================================================
  // Validate Session Before Every API
  // ======================================================

  if (isSessionExpired()) {
    toast.error('Session expired. Please log in again.', {
      toastId: TOAST_IDS.SESSION_EXPIRED,
      autoClose: 3000,
    });

    redirectToLogin(3000);

    return Promise.reject('Session expired');
  }

  // ======================================================
  // Non-SSO / Public APIs
  // ======================================================

  if (options.isAuthenticationNotRequired || isSSO === 'false' || !isSSO) {
    const authorization = options.pyuseridentifier || pyuseridentifier;

    options = {
      headers: {
        'Content-Type': 'text/xml',
        // pwd: password,
        Authorization: encryptData(
          `${authorization}:${password}`,
          CryptoJS.enc.Utf8.parse('App@123456789012')
        ),
        // Authorization: authorization,
        'X-Time-Zone': TIMEZONE,
      },
      ...options,
    };
  } else {
    let accesstoken = localStorage.getItem('accessToken');

    if (!accesstoken) {
      return;
    }

    options = {
      headers: {
        Authorization: `Bearer ${accesstoken}`,
        'X-Time-Zone': TIMEZONE,
      },
      ...options,
    };

    if (options.contentType) {
      options.headers['Content-Type'] = options.contentType;
      // options.body = options.data
    } else {
      options.headers['Content-Type'] = 'Application/json';
      options.headers['Accept'] = 'Application/json';
    }
  }

  // ======================================================
  // Success Handler
  // ======================================================

  const onSuccess = function (response) {
    return new Promise(function (resolve, reject) {
      if (response.data) {
        resolve(typeof response.data === 'string' ? JSON.parse(response.data) : response.data);
      } else {
        onError(response.status);
      }
    });
  };

  // ======================================================
  // Error Handler
  // ======================================================
  const SSOLogin = window.isSSO === true || window.isSSO === 'true';

  const onError = function (error) {
    if (error) {
      switch (error?.response?.status) {
        case 401: {
          if (SSOLogin && isSessionExpired()) {
            toast.error('Session expired. Please log in again.', {
              toastId: TOAST_IDS.SESSION_EXPIRED,
              autoClose: 3000,
            });

            redirectToLogin(3000);
          } else {
            toast.error('Authentication error, Redirecting to login page', {
              toastId: TOAST_IDS.AUTH_ERROR,
              autoClose: 4000,
            });
            redirectToLogin(3000);
          }

          break;
        }
        case 404:
          toast.error(`Please try again`);
          break;
        case 400:
          toast.error(`Validation Error`);
          break;
        default:
          toast.error(`Server Error, Contact Administrator`);
          break;
      }
    } else {
      toast.error(error.message);
    }
  };
  return client(options).then(onSuccess).catch(onError);
};

export default request;
